/**
 * Check-In contracts.
 *
 * A Check-In Session is a SM4RT-native intake workflow that runs upstream of
 * RepairShopr. Reception creates a draft session containing one or more child
 * Draft Jobs (one per device). The customer completes intake — in-store via a
 * QR-linked page or online via an emailed/SMS link — accepts terms and pays
 * preliminary charges, after which SM4RT orchestrates creation of the
 * RepairShopr customer, ticket(s), invoice and line items.
 *
 * The Check-In lifecycle is intentionally separate from the RepairShopr ticket
 * lifecycle so we keep an SM4RT-side audit trail and can recover or cancel
 * without partial-RS pollution.
 *
 * Storage shape (Dataverse):
 *   - cr001_check_in_sessionses     parent session
 *   - cr001_check_in_draft_jobses   one row per device on the session
 *   - cr001_check_in_photoses       one row per inspection photo
 *   - cr001_check_in_auditses       domain-level audit trail
 *
 * Per-step orchestration audit lives in the existing
 * cr001_integration_messageses + cr001_integration_stepsid tables.
 */

import type { TermsBodyFormat } from './termsFormat';

import type { SavedLineItem } from './workitem';

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

/**
 * Intake mode for a Check-In Session.
 *
 *  - `in_store`         — customer drops the device at reception; QR-linked
 *    customer-facing intake on a wall display.
 *  - `online`           — customer follows an emailed/SMS link to a public
 *    intake page; pays online, drops off later.
 *  - `express`          — staff-driven fast path for known business customers
 *    on monthly invoice agreements. No public intake, no terms acceptance,
 *    no money to collect; the goal is to get a printable check-in slip out
 *    fast so machines don't sit unlabelled. paymentState starts as
 *    'not_required' and the session jumps straight to inspection.
 *  - `in_store_return`  — customer brings back a device from a previously
 *    closed/resolved job (warranty, recurrence). Behaves like `in_store`
 *    (full payment + intake workflow), but requires `originalWorkItemId`
 *    on the session and is single-device-per-session by convention.
 *  - `in_store_dropoff` — customer drops items off for an existing, still
 *    in-progress job: either bringing a device back after parts arrived, or
 *    handing over an item the job needs (drive, paperwork, adapter, cable).
 *    Charges were already agreed/collected on the original job, so no new
 *    diagnostic/preliminary fee is raised — paymentState starts as
 *    'not_required' (like `express`). Requires `originalWorkItemId`, which
 *    here points at the OPEN original job (contrast `in_store_return`, whose
 *    original is closed/resolved). The drop-off attaches to that existing
 *    job rather than spawning a brand-new one.
 */
export type CheckInIntakeType = 'in_store' | 'online' | 'express' | 'in_store_return' | 'in_store_dropoff';

export type CheckInDeliveryMethod = 'email' | 'sms' | 'both';

export type CheckInDeviceType =
  | 'laptop'
  | 'desktop'
  | 'all_in_one'
  | 'phone'
  | 'tablet'
  | 'hard_drive'
  | 'usb_flash_drive'
  | 'console'
  /** Anything else. Always paired with `deviceTypeOther`, which says what it is. */
  | 'other';

/** Max length of `deviceTypeOther` — mirrored in sm4rt-api's draft-job service. */
export const CHECK_IN_DEVICE_TYPE_OTHER_MAX_LENGTH = 100;

/**
 * The one display name per device type. sm4rt-api's `DEVICE_TYPE_LABELS`
 * (ticket sheet) mirrors it. Prefer `checkInDeviceLabel`, which names an
 * `'other'` device by what it is.
 */
export const CHECK_IN_DEVICE_TYPE_LABELS: Record<CheckInDeviceType, string> = {
  laptop: 'Laptop',
  desktop: 'Desktop',
  all_in_one: 'All-in-One',
  phone: 'Phone',
  tablet: 'Tablet',
  hard_drive: 'Hard Drive',
  usb_flash_drive: 'USB Flash Drive',
  console: 'Console',
  other: 'Other',
};

/** "Drone" for an `'other'` device that says what it is, else the type's label. */
export function checkInDeviceLabel(device: {
  deviceType: CheckInDeviceType;
  deviceTypeOther?: string | null;
}): string {
  const described = device.deviceType === 'other' ? (device.deviceTypeOther || '').trim() : '';
  return described || CHECK_IN_DEVICE_TYPE_LABELS[device.deviceType] || device.deviceType;
}

/**
 * Per-device transaction mode.
 *
 *  - `diagnose_and_quote` — customer pays a diagnostic fee; SM4RT assesses
 *    and quotes the actual repair later. The default for the bulk of repair
 *    and data-recovery jobs.
 *  - `prepaid_in_full` — customer pays the full service cost upfront for a
 *    known-cost service (e.g. screen replacement, OS reinstall). No
 *    diagnostic fee. Express add-ons still apply.
 */
export type CheckInServiceMode = 'diagnose_and_quote' | 'prepaid_in_full';

export type CheckInInspectionStatus = 'not_started' | 'in_progress' | 'complete';

export type CheckInActorType = 'staff' | 'customer' | 'system';

/**
 * Whether a check-in produces ONE RepairShopr ticket covering every device, or
 * one ticket per device. Reception chooses it once, up front, on the create
 * page — it is never a per-device decision, which is what distinguishes it from
 * the retired per-device ticket-assignment picker it replaces.
 *
 *  - `single_job` — one RS ticket for all devices. Three new laptops for one
 *    business are one unit of work, invoiced once. The accepted cost is that
 *    RepairShopr then holds one status and one assignee for every device.
 *  - `job_per_device` — one RS ticket each. Today's behaviour, and what an
 *    absent value means, so no historical session changes meaning.
 *
 * Deliberately NOT an intake type. `intakeType` is load-bearing for
 * sm4rt-public-api's customer-facing terms path, where
 * `PUBLIC_TERMS_INTAKE_TYPES` is an exact allow-list and the terms-template
 * lookup has no family fallback — an `in_store_single` session could not accept
 * terms at all. See docs/architecture/checkin-ticket-grouping.md in sm4rt-api.
 */
export type CheckInTicketGrouping = 'single_job' | 'job_per_device';

export const CHECK_IN_TICKET_GROUPINGS: readonly CheckInTicketGrouping[] = [
  'single_job',
  'job_per_device',
] as const;

/** What an unset `cr001_ticket_grouping` means. The coercion itself lives
 *  server-side in `check_in_mappers`, so clients always receive a concrete
 *  value; this is here for form state that has to start somewhere. */
export const CHECK_IN_TICKET_GROUPING_DEFAULT: CheckInTicketGrouping = 'job_per_device';

/**
 * Lifecycle states for a Check-In Session. Distinct from RepairShopr ticket
 * status — this enum tracks the SM4RT intake workflow only.
 *
 * Allows free-form Dataverse values via `(string & {})` to mirror the JobStatus
 * convention in job.ts.
 */
export type CheckInLifecycleStatus =
  | 'draft_created'
  | 'awaiting_customer_intake'
  | 'intake_in_progress'
  | 'terms_accepted'
  | 'creating_jobs'
  | 'job_creation_failed'
  | 'awaiting_payment'
  | 'awaiting_bank_transfer'
  | 'payment_failed'
  | 'paid'
  | 'awaiting_physical_dropoff'
  | 'inspection_in_progress'
  | 'checked_in'
  | 'expired'
  | 'cancelled'
  | (string & {});

export type CheckInPaymentState =
  | 'not_required'
  | 'awaiting_payment'
  | 'awaiting_bank_transfer'
  | 'paid'
  | 'paid_overridden'
  /** Defer (Existing customer): the invoice's Xero pay link has been sent and
   *  diagnostics go ahead — nothing is held waiting on it. */
  | 'deferred'
  /** Defer (New customer): the invoice's Xero pay link has been sent and the
   *  ticket is held at `Waiting on Payment` until a technician moves it on. */
  | 'deferred_awaiting_payment'
  | 'failed'
  | (string & {});

/**
 * Domain-level audit event types written to cr001_check_in_auditses.
 * Per-step orchestration audit is recorded separately in
 * cr001_integration_stepsid by the orchestrator.
 */
export type CheckInAuditEventType =
  | 'session_created'
  | 'session_cancelled'
  | 'session_expired'
  | 'session_recovered'
  | 'draft_job_added'
  | 'draft_job_removed'
  | 'draft_job_updated'
  | 'charges_updated'
  | 'charges_locked'
  | 'customer_details_updated'
  | 'internal_notes_updated'
  | 'terms_accepted'
  /** Reception continued without a digital acceptance after handing the
   *  customer printed terms. Payload: termsVersion, reasonCode, handedOver;
   *  `reason` carries the label or the free text. Never an acceptance. */
  | 'terms_digital_acceptance_waived'
  /** The terms were printed for the customer (receipt or A4). Payload:
   *  document, target, role, printJobId, termsVersion. Separate from
   *  `document_printed`, which means the check-in paperwork. */
  | 'terms_printed'
  | 'terms_email_sent'
  | 'rs_customer_created'
  | 'rs_ticket_created'
  | 'rs_invoice_created'
  | 'rs_line_items_created'
  | 'payment_marked_paid'
  | 'payment_overridden'
  /** Reception chose Defer (Existing / New customer). Payload carries the
   *  mode, the masked destinations and, for a new customer, the RS tickets
   *  moved to `Waiting on Payment`. Never the pay link. */
  | 'payment_deferred'
  /** Reception chose Not Required (e.g. a screen repair — no diagnostics). */
  | 'payment_not_required'
  | 'payment_failed'
  | 'inspection_started'
  | 'inspection_updated'
  | 'inspection_completed'
  | 'photo_uploaded'
  | 'photo_removed'
  | 'orchestration_step_succeeded'
  | 'orchestration_step_failed'
  | 'orchestration_retried'
  | 'ticket_grouping_changed'
  | 'dropoff_ticket_bound'
  /** A check-in document actually went to a printer. Payload: document,
   *  copies, role, ticketId, printJobId, deviceCount. Only published prints —
   *  a skipped one records nothing. Written by sm4rt-api's
   *  `check_in_print_audit` for every surface, not just the check-in panel. */
  | 'document_printed'
  | 'check_in_completed'
  | (string & {});

// ---------------------------------------------------------------------------
// Inspection checklist
// ---------------------------------------------------------------------------

export type CheckInYesNo = 'yes' | 'no';

/**
 * A single Yes/No row on an inspection checklist with optional notes.
 */
export interface CheckInInspectionCheck {
  status: CheckInYesNo | null;
  notes: string | null;
}

export type LaptopManufacturer = string;
export type DesktopManufacturer =
  | 'Dell' | 'HP' | 'Lenovo' | 'MSI' | 'Acer' | 'Asus' | 'Custom'
  | (string & {});
export type AllInOneManufacturer =
  | 'Dell' | 'HP' | 'Lenovo' | 'MSI' | 'Acer' | 'Asus'
  | (string & {});
// Phone / tablet manufacturers are free-text (autocomplete) like laptops —
// the brand space is large and changes often.
export type PhoneManufacturer = string;
export type TabletManufacturer = string;
export type HardDriveManufacturer =
  | 'Seagate' | 'Western Digital' | 'Toshiba' | 'Samsung' | 'Crucial' | 'SanDisk' | 'HGST'
  | (string & {});
export type UsbFlashDriveManufacturer =
  | 'SanDisk' | 'Kingston' | 'Samsung' | 'Lexar' | 'PNY' | 'Verbatim' | 'Sony'
  | (string & {});
export type ConsoleManufacturer =
  | 'Sony' | 'Microsoft' | 'Nintendo' | 'Valve' | 'Other'
  | (string & {});
// Free text: by definition the device is not one we have a list for.
export type OtherDeviceManufacturer = string;

/**
 * Which identity fields the AI rig's label reading filled in, keyed to the
 * photo it came from (`CheckInPhoto.id`). A key is present while the value is
 * still exactly what the photo said; the page drops it the moment staff edit
 * that field, so it reads "from photo — check" until someone has looked.
 */
export interface CheckInInspectionAiFilled {
  manufacturer?: string | null;
  serialNumber?: string | null;
  modelNumber?: string | null;
  /**
   * Apple computers only: the EMC number printed beside the A-number on the
   * case ("EMC 3598"), which tells apart generations sharing an A-number.
   * Stored as captured; not resolved. Absent before 2026-10-10.
   */
  emcNumber?: string | null;
}

export interface LaptopInspection {
  deviceType: 'laptop';
  manufacturer: LaptopManufacturer | null;
  serialNumber: string | null;
  /**
   * The manufacturer's model number, as printed on the device or its label:
   * Apple's A-number (`A2338`), Surface's `Model 1866`, Lenovo's MTM
   * (`20XW0026UE`), HP's product number. Optional and never required to
   * complete. Absent on inspections saved before 2026-10-10.
   */
  modelNumber?: string | null;
  serialNotLegible: boolean;
  /**
   * Set when the device arrives brand-new in its unopened factory box.
   * The wizard skips the physical inspection steps (item status) when
   * this is true; staff can still record damage observations against the
   * box itself if they choose.
   */
  brandNewInBox?: boolean;
  /** Identity fields pre-filled from a photo's label — see CheckInInspectionAiFilled. */
  aiFilled?: CheckInInspectionAiFilled;
  damageChecks: {
    display: CheckInInspectionCheck;
    displayBezel: CheckInInspectionCheck;
    displayChassis: CheckInInspectionCheck;
    hinges: CheckInInspectionCheck;
    palmrest: CheckInInspectionCheck;
    bottomChassis: CheckInInspectionCheck;
  };
  itemStatusChecks: {
    allFeetPresent: CheckInInspectionCheck;
    allScrewsPresent: CheckInInspectionCheck;
    wobbles: CheckInInspectionCheck;
    usbItems: CheckInInspectionCheck;
    powerAdapter: CheckInInspectionCheck;
  };
  otherNotes: string | null;
}

export interface DesktopInspection {
  deviceType: 'desktop';
  manufacturer: DesktopManufacturer | null;
  serialNumber: string | null;       // Not required when manufacturer === 'Custom'
  /** See LaptopInspection.modelNumber. */
  modelNumber?: string | null;
  /** See LaptopInspection.emcNumber. */
  emcNumber?: string | null;
  /** "No visible serial" — offered on every device type since 2026-10-10. */
  serialNotLegible?: boolean;
  /** See LaptopInspection.brandNewInBox. */
  brandNewInBox?: boolean;
  /** Identity fields pre-filled from a photo's label — see CheckInInspectionAiFilled. */
  aiFilled?: CheckInInspectionAiFilled;
  checks: {
    allFeetPresent: CheckInInspectionCheck;
    usbItems: CheckInInspectionCheck;
  };
  otherNotes: string | null;
}

export interface AllInOneInspection {
  deviceType: 'all_in_one';
  manufacturer: AllInOneManufacturer | null;
  serialNumber: string | null;
  /** See LaptopInspection.modelNumber. */
  modelNumber?: string | null;
  /** See LaptopInspection.emcNumber. */
  emcNumber?: string | null;
  /** "No visible serial" — offered on every device type since 2026-10-10. */
  serialNotLegible?: boolean;
  /** See LaptopInspection.brandNewInBox. */
  brandNewInBox?: boolean;
  /** Identity fields pre-filled from a photo's label — see CheckInInspectionAiFilled. */
  aiFilled?: CheckInInspectionAiFilled;
  checks: {
    allFeetPresent: CheckInInspectionCheck;
    usbItems: CheckInInspectionCheck;
    screenDamage: CheckInInspectionCheck;
  };
  otherNotes: string | null;
}

/**
 * Phone inspection. Like all-in-one/desktop, the checks live in a single
 * `checks` group; the inspection wizard splits them into damage vs
 * item-status rows via INSPECTION_SCHEMA. `serialNumber` doubles as the
 * IMEI for phones.
 */
export interface PhoneInspection {
  deviceType: 'phone';
  manufacturer: PhoneManufacturer | null;
  serialNumber: string | null;       // Serial / IMEI
  /** See LaptopInspection.modelNumber. */
  modelNumber?: string | null;
  /** See LaptopInspection.emcNumber. */
  emcNumber?: string | null;
  serialNotLegible: boolean;
  /** See LaptopInspection.brandNewInBox. */
  brandNewInBox?: boolean;
  /** Identity fields pre-filled from a photo's label — see CheckInInspectionAiFilled. */
  aiFilled?: CheckInInspectionAiFilled;
  checks: {
    screenGlass: CheckInInspectionCheck;
    rearHousing: CheckInInspectionCheck;
    frameCorners: CheckInInspectionCheck;
    cameraLens: CheckInInspectionCheck;
    buttons: CheckInInspectionCheck;
    chargePort: CheckInInspectionCheck;
    simTray: CheckInInspectionCheck;
    accessories: CheckInInspectionCheck;
  };
  otherNotes: string | null;
}

export interface TabletInspection {
  deviceType: 'tablet';
  manufacturer: TabletManufacturer | null;
  serialNumber: string | null;
  /** See LaptopInspection.modelNumber. */
  modelNumber?: string | null;
  /** See LaptopInspection.emcNumber. */
  emcNumber?: string | null;
  serialNotLegible: boolean;
  /** See LaptopInspection.brandNewInBox. */
  brandNewInBox?: boolean;
  /** Identity fields pre-filled from a photo's label — see CheckInInspectionAiFilled. */
  aiFilled?: CheckInInspectionAiFilled;
  checks: {
    screenGlass: CheckInInspectionCheck;
    rearHousing: CheckInInspectionCheck;
    frameCorners: CheckInInspectionCheck;
    cameraLens: CheckInInspectionCheck;
    buttons: CheckInInspectionCheck;
    chargePort: CheckInInspectionCheck;
    accessories: CheckInInspectionCheck;
  };
  otherNotes: string | null;
}

/**
 * Hard drive (bare or external) — a data-recovery intake. Serial is optional
 * because drive labels are frequently worn or unreadable; capacity/interface
 * is captured in `otherNotes` (no dedicated field yet).
 */
export interface HardDriveInspection {
  deviceType: 'hard_drive';
  manufacturer: HardDriveManufacturer | null;
  serialNumber: string | null;       // Optional — labels are often worn
  /** See LaptopInspection.modelNumber. */
  modelNumber?: string | null;
  /** See LaptopInspection.emcNumber. */
  emcNumber?: string | null;
  /** "No visible serial" — offered on every device type since 2026-10-10. */
  serialNotLegible?: boolean;
  /** See LaptopInspection.brandNewInBox. */
  brandNewInBox?: boolean;
  /** Identity fields pre-filled from a photo's label — see CheckInInspectionAiFilled. */
  aiFilled?: CheckInInspectionAiFilled;
  checks: {
    enclosure: CheckInInspectionCheck;
    connector: CheckInInspectionCheck;
    dropImpact: CheckInInspectionCheck;
    cablePresent: CheckInInspectionCheck;
    caddyPresent: CheckInInspectionCheck;
  };
  otherNotes: string | null;
}

export interface UsbFlashDriveInspection {
  deviceType: 'usb_flash_drive';
  manufacturer: UsbFlashDriveManufacturer | null;
  serialNumber: string | null;       // Optional — many USB sticks carry no serial
  /** See LaptopInspection.modelNumber. */
  modelNumber?: string | null;
  /** See LaptopInspection.emcNumber. */
  emcNumber?: string | null;
  /** "No visible serial" — offered on every device type since 2026-10-10. */
  serialNotLegible?: boolean;
  /** See LaptopInspection.brandNewInBox. */
  brandNewInBox?: boolean;
  /** Identity fields pre-filled from a photo's label — see CheckInInspectionAiFilled. */
  aiFilled?: CheckInInspectionAiFilled;
  checks: {
    usbConnector: CheckInInspectionCheck;
    casing: CheckInInspectionCheck;
    capPresent: CheckInInspectionCheck;
  };
  otherNotes: string | null;
}

/** Games console. Serial required unless it is not legible. */
export interface ConsoleInspection {
  deviceType: 'console';
  manufacturer: ConsoleManufacturer | null;
  serialNumber: string | null;
  /** See LaptopInspection.modelNumber. */
  modelNumber?: string | null;
  /** See LaptopInspection.emcNumber. */
  emcNumber?: string | null;
  serialNotLegible: boolean;
  /** See LaptopInspection.brandNewInBox. */
  brandNewInBox?: boolean;
  /** Identity fields pre-filled from a photo's label — see CheckInInspectionAiFilled. */
  aiFilled?: CheckInInspectionAiFilled;
  checks: {
    casing: CheckInInspectionCheck;
    ports: CheckInInspectionCheck;           // HDMI / USB
    discDrive: CheckInInspectionCheck;       // Disc drive / cartridge slot
    controllers: CheckInInspectionCheck;     // Present
    powerCable: CheckInInspectionCheck;      // Present
    hdmiCable: CheckInInspectionCheck;       // Present
  };
  otherNotes: string | null;
}

/**
 * Any device without its own type. What it *is* lives on the draft job
 * (`deviceTypeOther`), not here; serial is optional because there is no
 * telling whether it has one.
 */
export interface OtherDeviceInspection {
  deviceType: 'other';
  manufacturer: OtherDeviceManufacturer | null;
  serialNumber: string | null;
  /** See LaptopInspection.modelNumber. */
  modelNumber?: string | null;
  /** See LaptopInspection.emcNumber. */
  emcNumber?: string | null;
  /** "No visible serial" — offered on every device type since 2026-10-10. */
  serialNotLegible?: boolean;
  /** See LaptopInspection.brandNewInBox. */
  brandNewInBox?: boolean;
  /** Identity fields pre-filled from a photo's label — see CheckInInspectionAiFilled. */
  aiFilled?: CheckInInspectionAiFilled;
  checks: {
    casing: CheckInInspectionCheck;          // Casing / body
    screen: CheckInInspectionCheck;          // Screen (if any)
    ports: CheckInInspectionCheck;           // Ports / connectors
    powerAdapter: CheckInInspectionCheck;    // Power adapter / charger present
    accessories: CheckInInspectionCheck;     // Present
  };
  otherNotes: string | null;
}

export type CheckInInspection =
  | LaptopInspection
  | DesktopInspection
  | AllInOneInspection
  | PhoneInspection
  | TabletInspection
  | HardDriveInspection
  | UsbFlashDriveInspection
  | ConsoleInspection
  | OtherDeviceInspection;

// ---------------------------------------------------------------------------
// Charges & terms snapshots
// ---------------------------------------------------------------------------

/**
 * A single preliminary charge line on a draft job. Mirrors the SavedLineItem
 * shape so dashboard rendering and downstream RS line-item creation can reuse
 * existing utilities.
 */
export type CheckInChargeLine = SavedLineItem & {
  templateKey?: string | null;       // From the source ticket-type-template, when applicable
  pricingRule?: string | null;       // Hook name from PRICING_RULE_REGISTRY
};

/**
 * Frozen-at-acceptance copy of a single charge line. Lives inside the
 * termsAcceptance snapshot so we have an immutable record of what the
 * customer agreed to.
 */
export interface CheckInChargeSnapshotItem {
  draftJobId: string;
  category: string;
  prefixUserBase: string;
  lineDetail: string;
  quantity: number;
  unitCost: number;
  subtotal: number;
  finalTotal: number;
  afterHours: boolean;
  nonChargeable: boolean;
}

/**
 * Why reception continued a check-in without the customer accepting the terms
 * digitally. Mirrored in sm4rt-api's `WAIVER_REASONS`; `other` needs text.
 */
export type CheckInTermsWaiverReasonCode = 'no_phone' | 'battery_flat' | 'too_confusing' | 'other';

export const CHECK_IN_TERMS_WAIVER_REASONS: ReadonlyArray<{ code: CheckInTermsWaiverReasonCode; label: string }> = [
  { code: 'no_phone', label: "Doesn't have phone with them" },
  { code: 'battery_flat', label: 'Phone battery is flat' },
  { code: 'too_confusing', label: 'Too confusing' },
  { code: 'other', label: 'Other' },
];

/** Max length of the free-text reason — mirrored in sm4rt-api. */
export const CHECK_IN_TERMS_WAIVER_REASON_MAX_LENGTH = 200;

/**
 * Printed terms handed to a customer who could not accept them digitally
 * ("Continue without digital acceptance"). **Not an acceptance**, and never
 * stored as one: it records which terms were printed and handed over, why,
 * by whom and when.
 */
export interface CheckInTermsNotice {
  termsTitle: string;
  termsVersion: string;
  /** The terms as printed — template body with `{{variables}}` resolved. */
  termsBody: string;
  termsBodyFormat: TermsBodyFormat;
  reasonCode: CheckInTermsWaiverReasonCode;
  reasonLabel: string;
  /** Free text — required for `other`, optional otherwise. */
  reasonText: string | null;
  /** Always true: the endpoint refuses without the hand-over tick. */
  handedOver: true;
  recordedAt: string;                 // ISO 8601, Australia/Sydney
  recordedBy: string;                 // UPN
}

/** Whether "Continue without digital acceptance" is offered — server-computed. */
export interface CheckInContinueWithoutDigitalAcceptanceState {
  available: boolean;
  /** Why not, in words reception can act on. Null when available. */
  reason: string | null;
}

/** POST /api/check-in-sessions/{id}/terms/continue-without-digital-acceptance */
export interface CheckInContinueWithoutDigitalAcceptanceRequest {
  reasonCode: CheckInTermsWaiverReasonCode;
  reasonText?: string | null;
  handedOver: true;
}

/** POST /api/check-in-sessions/{id}/terms/print */
export interface CheckInPrintTermsRequest {
  target: 'receipt' | 'a4';
}

export interface CheckInTermsAcceptance {
  acceptedAt: string;                 // ISO 8601
  ipAddress: string;
  termsVersion: string;               // e.g. "2026-05-10"
  /**
   * The terms text as the customer saw it: template body with every
   * `{{variable}}` already substituted. Frozen so the template can change
   * afterwards without altering what was accepted.
   */
  termsBody: string;
  /** How `termsBody` was displayed — re-rendering it later needs to know. */
  termsBodyFormat: TermsBodyFormat;
  /**
   * The unresolved template body behind `termsBody`, kept alongside it.
   *
   * `termsBody` alone is the record of what was agreed; this plus
   * {@link termsValues} is what makes it auditable — you can show that the
   * frozen text is what the template and the session's own values produce, and
   * not something assembled by hand.
   *
   * **Absent when the template had no `{{variables}}`**, because then it would
   * be a byte-for-byte copy of `termsBody`. Terms run to five figures of
   * characters and the column is finite, so the duplicate is not free: a
   * 14,000-character body against what was then a 2,000-character column took
   * terms acceptance down in production on 2026-09-06. Absent means "the
   * template was the text".
   */
  termsBodySource?: string;
  /** Every variable value used, pre-formatted. Absent with `termsBodySource`. */
  termsValues?: Record<string, string>;
  acceptedTotal: number;              // Dollar total at acceptance
  customerSnapshot: {
    name: string;
    email: string;
    phone: string;
    businessName: string | null;
  };
  chargesSnapshot: CheckInChargeSnapshotItem[];
}

// ---------------------------------------------------------------------------
// Photo
// ---------------------------------------------------------------------------

/**
 * Which guided inspection shot a photo is. Each device type offers its own
 * list (sm4rt-web's INSPECTION_SCHEMA); every one starts with `serial_label`
 * (the photo the AI rig reads, and the one completion needs unless "No visible
 * serial" is chosen) and ends with any number of `area_of_interest` close-ups.
 * Every other shot holds a single photo — a retake replaces it.
 */
export type CheckInPhotoShot =
  | 'serial_label'
  | 'area_of_interest'
  | 'front_open'
  | 'front_closed'
  | 'lhs_closed'
  | 'rhs_closed'
  | 'back_closed'
  | 'underside_closed'
  | 'top_closed'
  | 'front'
  | 'back'
  | 'top'
  | 'rear'
  | 'lhs'
  | 'rhs'
  | 'usb_port_side'
  | 'connector'
  | 'accessories';

/**
 * The AI rig's reading of a photo's label (psar01, local vision model).
 * Null on photos uploaded before the feature. `pending` until the rig posts;
 * then `done` (outcome `read` or `no_label`) or `failed`. Only a `read`
 * carries a manufacturer / serial — sm4rt-web pre-fills Identify's BLANK
 * fields from it.
 */
export interface CheckInPhotoAiReading {
  status: 'pending' | 'done' | 'failed';
  outcome: 'read' | 'no_label' | 'failed' | null;
  manufacturer: string | null;
  serialNumber: string | null;
  /** The device's model number off the label. Absent from rigs predating it. */
  modelNumber?: string | null;
  /** An Apple computer's EMC number off the case. Absent from rigs predating it. */
  emcNumber?: string | null;
  /** 0–1, as the model reported it. */
  confidence: number | null;
  /** The VISION model that made the reading — not the device's model (`modelNumber`). */
  model: string | null;
  analysedAt: string | null;          // ISO 8601
}

export interface CheckInPhoto {
  id: string;                         // DV cr001_check_in_photosid
  draftJobId: string;
  blobUrl: string;                    // Unsigned blob path; signed URL produced on read
  signedUrl?: string | null;          // Populated by the API when returning to the client
  /**
   * The same photo as a download — a short-lived link that makes the browser
   * save it (`Content-Disposition: attachment`) instead of opening it. Needed
   * because the photo is cross-origin, where `<a download>` is ignored.
   */
  downloadUrl?: string | null;
  originalFilename: string | null;
  widthPx: number | null;
  heightPx: number | null;
  sizeBytes: number | null;
  uploadedBy: string;                 // UPN
  uploadedAt: string;                 // ISO 8601
  /** Absent from servers predating the AI reading. */
  aiReading?: CheckInPhotoAiReading | null;
  /** Which guided shot this is. Null on photos taken before shots existed. */
  shot?: CheckInPhotoShot | null;
  /** What an area-of-interest photo shows ("Cracked left hinge"); max 200. */
  caption?: string | null;
}

// ---------------------------------------------------------------------------
// Draft job
// ---------------------------------------------------------------------------

/**
 * Full draft-job entity returned by detail endpoints.
 */
export interface CheckInDraftJob {
  id: string;
  sessionId: string;
  displayIndex: number;

  deviceType: CheckInDeviceType;
  /**
   * What the device is, when `deviceType` is `'other'` ("Drone"). Required
   * then, null for every other type. Shown in place of the word "Other"
   * wherever a device is named — use `checkInDeviceLabel`.
   */
  deviceTypeOther: string | null;
  serviceMode: CheckInServiceMode;
  serviceTemplateId: string | null;
  serviceSummary: string;
  requirements: string | null;
  customerVisibleDetails: string | null;
  internalNotes: string | null;       // Staff-only

  preliminaryCharges: CheckInChargeLine[];

  repairShoprTicketId: string | null; // Set by orchestrator step rs_create_tickets, or by sm4rt-api when it binds a drop-off to the job it extends
  repairShoprTicketNumber: string | null; // Human-facing RS ticket number; captured alongside the id at creation/link. Null for legacy rows (fall back to id for display).

  inspection: CheckInInspection | null;
  inspectionStatus: CheckInInspectionStatus;
  inspectionCompletedAt: string | null;
  inspectionCompletedBy: string | null;

  photos: CheckInPhoto[];             // Hydrated when reading the draft-job detail

  createdAt: string;
  updatedAt: string;
}

/**
 * Lightweight summary of a draft job for the work-item dashboard projection.
 * Excludes internalNotes and inspection (staff-only) and the photos array.
 */
export interface CheckInDraftJobSummary {
  id: string;
  displayIndex: number;
  deviceType: CheckInDeviceType;
  /** See CheckInDraftJob.deviceTypeOther. */
  deviceTypeOther: string | null;
  serviceSummary: string;
  repairShoprTicketId: string | null;
  repairShoprTicketNumber: string | null;
  inspectionStatus: CheckInInspectionStatus;
  chargesTotal: number;
  hasPhotos: boolean;
}

/**
 * A device as it appears on the Check-Ins list page — the draft-job summary
 * plus the make/serial that identifies the physical machine.
 *
 * The identity fields are lifted out of the draft job's inspection blob
 * server-side. The list endpoint already loads every draft-job row to count
 * them, so carrying the identity costs no extra query and saves the client
 * fetching each session in full just to label a row.
 *
 * Note this is a *projection*: the serial lives inside `inspection` on the
 * draft job and is not separately queryable, so it can be displayed and
 * filtered client-side but not searched server-side.
 */
export interface CheckInSessionListDevice extends CheckInDraftJobSummary {
  manufacturer: string | null;
  serialNumber: string | null;
  modelNumber: string | null;
  /** Only laptop/phone/tablet inspections carry the flag; false elsewhere. */
  serialNotLegible: boolean;
}

// ---------------------------------------------------------------------------
// Audit entry
// ---------------------------------------------------------------------------

export interface CheckInAuditEntry {
  id: string;
  sessionId: string;
  draftJobId: string | null;
  eventType: CheckInAuditEventType;
  actorType: CheckInActorType;
  actorId: string | null;             // UPN, customer email, or system component name
  previousValue: string | null;       // JSON-encoded
  newValue: string | null;            // JSON-encoded
  reason: string | null;
  correlationId: string | null;       // FK to cr001_integration_messagesid where relevant
  occurredAt: string;
}

// ---------------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------------

/**
 * Lightweight list-row for GET /api/check-in-sessions. Excludes child
 * draft-jobs, photos, audit, and internal notes.
 */
export interface CheckInSessionListItem {
  id: string;
  sessionNumber: string;              // Display id, e.g. "CI-2026-0042"

  intakeType: CheckInIntakeType;
  lifecycleStatus: CheckInLifecycleStatus;
  paymentState: CheckInPaymentState;
  deliveryMethod: CheckInDeliveryMethod | null;

  /**
   * Given and family name, captured separately at intake. These are the
   * authoritative fields — `customerName` is derived from them as
   * "first last" and kept only because ~30 read sites (terms variables,
   * the wall display, RS payloads, print slips) still want one string.
   * Nullable for historical sessions created before the split; readers
   * that need a name should fall back to `customerName`.
   */
  customerFirstName: string | null;
  customerLastName: string | null;
  /** Derived from `customerFirstName` + `customerLastName`. Never write it directly. */
  customerName: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  businessName: string | null;

  /**
   * Customer designation captured at session-create time from the
   * customer-lookup picker — raw wire value ("Core" | "Core+" | other
   * | null). Drives the default-no-diagnostic-fee behaviour in the
   * preliminary-charges editor (Core / Core+ customers are billed via
   * monthly statement, so we don't auto-charge a diag fee). Snapshotted
   * onto the session so the post-save edit-device flow can apply the
   * same suppression without re-fetching the customer record.
   */
  customerDesignation: string | null;

  /**
   * For `in_store_return` and `in_store_dropoff` sessions: the WorkItem id of
   * the original job this session is linked to. For a return the original is
   * closed/resolved; for a drop-off it is the open, in-progress job the items
   * belong to. Null for all other intake types.
   */
  originalWorkItemId: string | null;

  /**
   * One ticket for every device, or one each. Never null off the wire — the
   * server coerces an unset column to `job_per_device`, which is what a
   * session written before the choice existed did.
   */
  ticketGrouping: CheckInTicketGrouping;

  draftJobCount: number;
  preliminaryChargesTotal: number;

  /**
   * The devices on this session. Present on list responses only — the detail
   * response carries the full `draftJobs` instead, which is why
   * `CheckInSession` omits this.
   */
  devices: CheckInSessionListDevice[];

  expiresAt: string;                  // ISO; createdAt + 7d for drafts
  tokenExpiresAt: string;
  cancelledAt: string | null;
  completedAt: string | null;

  // System flags
  requiresAttention: boolean;
  retryAvailable: boolean;
  inspectionIncomplete: boolean;

  // External references (denormalised for list views)
  repairShoprCustomerId: string | null;
  repairShoprInvoiceId: string | null;
  xeroOnlineInvoiceUrl: string | null;

  createdAt: string;
  updatedAt: string;
  createdBy: string;                  // UPN of reception user who created the draft
}

/**
 * Full session entity with children. Returned by detail/audit endpoints.
 */
export interface CheckInSession extends Omit<CheckInSessionListItem, 'devices'> {
  // Token (omitted from public/customer responses in the API layer)
  token: string;

  // Customer-visible field
  customerNotes: string | null;
  // Staff-only field
  internalNotes: string | null;

  // External references (full set)
  repairShoprInvoiceId: string | null;
  /**
   * The invoice's display number — what `/invoice/{number}` takes and what a
   * person quotes. **Detail only**, and resolved at read time from
   * `repairShoprInvoiceId` rather than stored, so it covers historical sessions
   * too.
   *
   * Null for roughly the first five minutes of a session's life, while the
   * rs2dv sync lands the invoice row, and null on the list projection always
   * (the lookup is one query per session). Fall back to the RepairShopr link,
   * which takes the id — see `sessionInvoiceRef` in sm4rt-web.
   */
  repairShoprInvoiceNumber: string | null;
  xeroInvoiceId: string | null;
  paymentReference: string | null;
  paymentOverriddenBy: string | null;
  paymentOverriddenAt: string | null;
  assistedByUserId: string | null;
  integrationMessageId: string | null;

  /**
   * The customer told us at check-in that something they are leaving is worth
   * {@link CHECK_IN_HIGH_VALUE_THRESHOLD} or more.
   *
   * The check-in terms make this the trigger for **not disposing of the device
   * without an order from NCAT** (the NSW Civil and Administrative Tribunal), so
   * it has to be visible before anyone deals with an uncollected item. Nothing in
   * the estate enforces that yet — there is no disposal workflow — which is
   * precisely why it is captured: the promise predates the machinery.
   *
   * Session-level rather than per-device, because the customer-facing flow
   * exposes no device list to attach it to. It therefore over-protects: every
   * device on the session needs the order, which is the safe direction.
   *
   * A boolean at the threshold, not a declared amount. The consequence, accepted
   * deliberately: it means "high value relative to the threshold in force when
   * the row was written", so a change to that figure leaves older rows not
   * self-describing.
   *
   * False on sessions predating the question, and indistinguishable from a
   * customer who left the box unticked.
   */
  highValueDeclared: boolean;

  termsAcceptance: CheckInTermsAcceptance | null;
  /** Printed terms handed over instead of a digital acceptance. Absent from older servers. */
  termsNotice?: CheckInTermsNotice | null;
  /** Drives the "Continue without digital acceptance" button. Absent from older servers. */
  continueWithoutDigitalAcceptance?: CheckInContinueWithoutDigitalAcceptanceState;

  /**
   * Whether reception's Create Tickets action is available, computed
   * server-side so the button and the endpoint share one rule.
   */
  ticketCreation: CheckInTicketCreationState;

  draftJobs: CheckInDraftJob[];
  audit: CheckInAuditEntry[];
}

/**
 * Drives the Create Tickets button on the session detail page.
 *
 * "Start on wall" shows the QR on a session with no devices, so the customer
 * can accept the terms before reception has typed the machine in. Ticket
 * creation is therefore a separate phase the receptionist triggers once the
 * devices are captured — it needs a matched customer and at least one device
 * without a ticket, and deliberately NOT a completed device inspection.
 */
export interface CheckInTicketCreationState {
  canCreate: boolean;
  /** Why not, for the button's tooltip. Null when `canCreate` is true. */
  reason: string | null;
  /** Captured devices still without a RepairShopr ticket. */
  pendingDraftJobCount: number;
}

// ---------------------------------------------------------------------------
// API request / response DTOs
// ---------------------------------------------------------------------------

export interface CheckInSessionCreate {
  intakeType?: CheckInIntakeType;     // Defaults server-side to 'in_store'
  deliveryMethod?: CheckInDeliveryMethod;
  /**
   * Preferred over `customerName`. When either is supplied the server
   * derives `customerName`; `customerName` alone is still accepted (and
   * split on the last space) so existing callers keep working.
   */
  customerFirstName?: string;
  customerLastName?: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  businessName?: string;
  customerNotes?: string;
  internalNotes?: string;
  /**
   * Customer designation captured from the customer-lookup picker —
   * wire value ("Core" | "Core+" | other). When the picked customer is
   * Core or Core+ the check-in form omits the auto-diagnostic fee.
   * Optional; omitted/null is treated as "no designation".
   */
  customerDesignation?: string | null;
  /**
   * RepairShopr customer id captured from the customer-lookup picker.
   * Optional — when set, the orchestrator skips the email/phone match
   * step and the session-detail "link existing RS ticket" picker can
   * query RS for that customer's open tickets immediately.
   */
  repairShoprCustomerId?: string | null;
  /**
   * Required when `intakeType === 'in_store_return'` or `'in_store_dropoff'`;
   * rejected for all other intake types. Identifies the original WorkItem the
   * session is linked to — closed/resolved for a return, open/in-progress for
   * a drop-off.
   */
  originalWorkItemId?: string;
  /** Omitted = `job_per_device`. The create page always sends one: the choice
   *  is the tile, and a grouping nobody picked is invisible by the time the
   *  tickets exist. */
  ticketGrouping?: CheckInTicketGrouping;
  draftJobs?: CheckInDraftJobCreate[]; // Optional initial children
}

export interface CheckInSessionUpdate {
  deliveryMethod?: CheckInDeliveryMethod | null;
  internalNotes?: string | null;
  customerNotes?: string | null;
  // Pre-lock fields only; once intake starts, server rejects structural edits.
  customerFirstName?: string | null;
  customerLastName?: string | null;
  /** Derived server-side when first/last are sent. */
  customerName?: string | null;
  customerEmail?: string | null;
  customerPhone?: string | null;
  businessName?: string | null;
  customerDesignation?: string | null;
  /**
   * Allow patching the RepairShopr customer match — used when reception
   * (re-)picks the customer via the lookup on the session-detail page.
   * Pre-lock only.
   */
  repairShoprCustomerId?: string | null;
  /**
   * Changeable until any device carries a ticket, then refused — gated on the
   * ticket existing rather than on the intake lock, so a mis-picked tile is
   * recoverable without cancelling the check-in and losing an OTP-verified
   * mobile and an accepted terms snapshot with it.
   */
  ticketGrouping?: CheckInTicketGrouping;
}

export interface CheckInSessionCancelRequest {
  reason: string;
}

export interface CheckInSessionRecoverRequest {
  // Empty body. Server regenerates the token, audits the recovery.
}

export interface CheckInDraftJobCreate {
  deviceType: CheckInDeviceType;
  /** Required when `deviceType` is `'other'`; ignored otherwise. */
  deviceTypeOther?: string | null;
  serviceMode?: CheckInServiceMode;     // Defaults server-side to 'diagnose_and_quote'.
  serviceTemplateId?: string | null;
  /**
   * Required, with one exception: on a `single_job` session a device after the
   * first may omit it and the server copies device 1's, because in that
   * grouping the summary describes the job rather than the device.
   */
  serviceSummary?: string;
  requirements?: string | null;
  customerVisibleDetails?: string | null;
  internalNotes?: string | null;
  preliminaryCharges?: CheckInChargeLine[];
}

export interface CheckInDraftJobUpdate {
  // Pre-lock fields (rejected by server after intake starts):
  /** Only on an `'other'` device, and never blank. */
  deviceTypeOther?: string;
  serviceMode?: CheckInServiceMode;
  serviceTemplateId?: string | null;
  serviceSummary?: string;
  requirements?: string | null;
  customerVisibleDetails?: string | null;
  internalNotes?: string | null;
  preliminaryCharges?: CheckInChargeLine[];

  // Always-editable inspection block (staff-only):
  inspection?: CheckInInspection | null;
  inspectionStatus?: CheckInInspectionStatus;

  // The RepairShopr ticket refs. Normally written by the orchestrator's
  // `rs_create_tickets`, or by sm4rt-api when it binds a drop-off to the job
  // it extends; patchable for a correction. Clearing the id clears the number.
  repairShoprTicketId?: string | null;
  repairShoprTicketNumber?: string | null;
}

export interface CheckInPaymentMarkPaidRequest {
  paymentReference: string;           // EFTPOS receipt # / Xero payment id / bank ref
}

/** @deprecated Superseded by {@link CheckInPaymentDeferRequest}; the route
 *  stays until no client calls it. */
export interface CheckInPaymentOverrideRequest {
  reason: string;
  paymentReference?: string;
}

/**
 * What reception chose instead of taking payment at the counter.
 *
 * - `existing_customer` — send the invoice's Xero pay link; diagnostics go
 *   ahead (the message doesn't say they wait).
 * - `new_customer` — send the pay link, saying diagnostics start once it is
 *   paid, and hold every ticket at `Waiting on Payment`.
 *
 * Both Defer modes require the RS invoice to exist, and are refused (409)
 * while it is not yet in Xero — retry shortly.
 * - `not_required` — no diagnostics (e.g. a screen repair). Payment state
 *   only; charges and invoice are untouched.
 */
export type CheckInPaymentDeferMode = 'existing_customer' | 'new_customer' | 'not_required';

export type CheckInPaymentLinkChannel = 'email' | 'sms';

/** POST /api/check-in-sessions/{id}/payment/defer */
export interface CheckInPaymentDeferRequest {
  mode: CheckInPaymentDeferMode;
  /** Optional free text, recorded on the audit row. */
  note?: string;
  /** Both Defer modes — at least one. Ignored for `not_required`. */
  channels?: CheckInPaymentLinkChannel[];
  /** With `email` — defaults to the session's customerEmail. */
  email?: string;
  /** With `sms` — defaults to the session's customerPhone. */
  mobile?: string;
}

export interface CheckInPaymentLinkDelivery {
  channel: CheckInPaymentLinkChannel;
  sent: boolean;
  /** Masked, e.g. `s***@example.com` / `***5678`. Never the full address. */
  maskedDestination: string;
  /** Set when `sent` is false. */
  error?: string;
}

export interface CheckInPaymentDeferResponse {
  session: CheckInSession;
  /** Both Defer modes. At least one entry has `sent: true`. */
  deliveries: CheckInPaymentLinkDelivery[];
  /** Non-fatal problems the user should see — a channel that failed, or an
   *  RS ticket whose status could not be moved to `Waiting on Payment`. */
  warnings: string[];
}

export interface CheckInRetryResponse {
  integrationMessageId: string;
  enqueuedAt: string;
}

/**
 * Response from POST /api/check-in-sessions/{id}/tickets — reception's
 * Create Tickets action. Enqueues the tickets phase of the orchestration;
 * unlike the retry endpoint it does not move the session's lifecycle, because
 * payment may already have been taken.
 */
export interface CheckInCreateTicketsResponse {
  integrationMessageId: string;
  enqueuedAt: string | null;
  /** Devices the enqueued run will raise a ticket for. */
  pendingDraftJobCount: number;
}

/**
 * Row in the "Returns" panel on a WorkItem detail page. Each entry represents
 * a Check-In Session whose `originalWorkItemId` points at the WorkItem being
 * viewed — either an `in_store_return` (original is closed/resolved) or an
 * `in_store_dropoff` (original is open/in-progress).
 *
 * Returned by GET /api/workitems/{id}/returns.
 */
export interface CheckInReturnSummary {
  sessionId: string;
  sessionNumber: string;
  lifecycleStatus: CheckInLifecycleStatus;
  /** RS ticket id of the Return's single draft job, once orchestrated. */
  repairShoprTicketId: string | null;
  /** RS ticket number for display; null until the orchestrator creates the ticket. */
  ticketNumber: string | null;
  createdAt: string;
  createdBy: string;
}

export interface CheckInReturnListResponse {
  items: CheckInReturnSummary[];
}

/**
 * Response body from POST /api/check-in-sessions/{id}/complete when the
 * server refuses the call because one or more draft jobs are still unresolved.
 * HTTP status is 409.
 */
export interface CheckInCompletePreconditionFailure {
  error: 'ticket_assignment_incomplete';
  unresolvedDraftJobIds: string[];
}

/**
 * Success body from POST /api/check-in-sessions/{id}/complete — the session as
 * it now stands.
 *
 * It used to carry `autoPrint`, the result of firing `check_in.device_checked_in`
 * at completion. That trigger is retired (sm4rt-api, 2026-10-15): check-in
 * paperwork now prints when the Dataverse job behind each ticket exists
 * (`check_in.job_created` / `check_in.job_ready`), which is usually not the
 * moment anyone presses Complete. Whether it came out is read from the
 * session's `document_printed` audit rows (the Print section), not from this
 * response. Kept as a named alias so callers' imports keep compiling.
 */
export type CompleteCheckInSessionResponse = CheckInSession;

export interface CheckInPaginationMeta {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface CheckInSessionListResponse {
  items: CheckInSessionListItem[];
  pagination: CheckInPaginationMeta;
}

export interface CheckInAuditListResponse {
  items: CheckInAuditEntry[];
}

// ---------------------------------------------------------------------------
// Public-endpoint DTOs (token-protected, customer-facing)
//
// Implemented by sm4rt-api `api/public_check_in` (FR2 of
// external-checkin-swa-requirements.md). Consumed by the external Check-In SWA.
// The projection is deliberately narrow: device / inspection / per-line-charge
// detail and all staff-only fields are stripped server-side — see §8
// (trimmed-DTO leakage). The ONLY money exposed is a single `amountDue`, and
// only for the `online` intake type.
// ---------------------------------------------------------------------------

/**
 * Customer-facing view of a session. Staff-only fields (token, internalNotes,
 * draft-job device/inspection detail, photos, full audit) and per-line charge
 * breakdowns are all stripped before this is returned by the public endpoints.
 */
export interface CheckInPublicSession {
  sessionNumber: string;
  intakeType: CheckInIntakeType;
  lifecycleStatus: CheckInLifecycleStatus;
  paymentState: CheckInPaymentState;

  /**
   * Null on sessions that predate the first/last split, and on staff-created
   * sessions where reception only had one name to go on — the intake form
   * falls back to splitting `customerName` to prefill its two fields.
   */
  customerFirstName: string | null;
  customerLastName: string | null;
  /** Derived from first + last. Kept for display paths that want one string. */
  customerName: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  businessName: string | null;
  customerNotes: string | null;

  /**
   * Single amount payable. Populated for `online` (remote) sessions; `null`
   * for `in_store` (which pays EFTPOS at the counter). No per-device / per-line
   * breakdown is ever exposed.
   */
  amountDue: number | null;

  termsAcceptance: CheckInPublicTermsAcceptance | null;
  xeroOnlineInvoiceUrl: string | null;

  /**
   * Tenant policy: whether this check-in must confirm its mobile with a
   * one-time code before proceeding. Evaluated per read and never stored on the
   * session, which is what lets an admin switching it off release anyone
   * currently waiting on a code.
   *
   * The gate applies to the QR flow only — the reception tablet (`?kiosk=1`) is
   * the fallback for anyone who cannot verify, and skips it. That exemption is
   * enforced in the page, not the API: both paths are the same anonymous
   * endpoints and the server cannot tell them apart.
   *
   * Always false while the Dataverse columns are absent, because a gate that
   * could never be satisfied would wall off every QR check-in.
   */
  mobileVerificationRequired: boolean;

  /**
   * Whether this session's mobile was confirmed by a one-time code. A fact
   * about the session, written in the trusted zone by sm4rt-api's
   * `otp.verified` consumer — never by the public API, whose write allow-list
   * excludes it.
   *
   * What it attests is narrow: someone had access to that number at the moment
   * the terms were accepted. Not ownership — phones are shared, borrowed and
   * employer-issued.
   *
   * The verification *timestamp* is deliberately not exposed here. It is
   * evidentiary and belongs to the staff surfaces.
   */
  mobileNumberVerifiedByOtp: boolean;

  /**
   * Where a disposal notice under s27 of the Uncollected Goods Act 2005 (NSW)
   * is sent. The customer's own choice; `sms` unless they pick otherwise, and
   * on every session predating the setting.
   *
   * SMS or email only. The check-in terms commit to serving notice "by SMS or
   * email", so a landline is not an address they permit serving to.
   */
  disposalNoticeChannel: CheckInDisposalNoticeChannel;

  /**
   * The customer told us at check-in that something they are leaving is worth
   * {@link CHECK_IN_HIGH_VALUE_THRESHOLD} or more.
   *
   * The check-in terms make this the trigger for **not disposing of the device
   * without an order from NCAT** (the NSW Civil and Administrative Tribunal), so
   * it has to be visible before anyone deals with an uncollected item. Nothing in
   * the estate enforces that yet — there is no disposal workflow — which is
   * precisely why it is captured: the promise predates the machinery.
   *
   * Session-level rather than per-device, because the customer-facing flow
   * exposes no device list to attach it to. It therefore over-protects: every
   * device on the session needs the order, which is the safe direction.
   *
   * A boolean at the threshold, not a declared amount. The consequence, accepted
   * deliberately: it means "high value relative to the threshold in force when
   * the row was written", so a change to that figure leaves older rows not
   * self-describing.
   *
   * False on sessions predating the question, and indistinguishable from a
   * customer who left the box unticked.
   */
  highValueDeclared: boolean;

  expiresAt: string;
  tokenExpiresAt: string;
}

/** Channels a disposal notice may be served to. See `disposalNoticeChannel`. */
export type CheckInDisposalNoticeChannel = 'sms' | 'email';

/**
 * The value at or above which a customer's declaration triggers the NCAT
 * requirement in the check-in terms.
 *
 * One constant so the question the customer is asked, and anything that ever
 * acts on the answer, cannot drift apart. Changing it is a one-line edit — but
 * note that `highValueDeclared` rows written before the change recorded a
 * declaration against the OLD figure and are not self-describing, so a change
 * needs a decision about historical rows, not just this number.
 */
export const CHECK_IN_HIGH_VALUE_THRESHOLD = 20000;

/**
 * Customer-safe view of a terms acceptance. The stored acceptance snapshot
 * also embeds `customerSnapshot` and a per-line `chargesSnapshot` (and the
 * frozen terms body) — none of which are exposed publicly.
 */
export interface CheckInPublicTermsAcceptance {
  acceptedAt: string;
  termsVersion: string;
  acceptedTotal: number;
}

/**
 * Internal-only shape (NOT returned by any public endpoint). Retained for
 * reference; the public session deliberately omits all draft-job detail.
 */
export interface CheckInPublicDraftJob {
  id: string;
  deviceType: CheckInDeviceType;
  serviceSummary: string;
  requirements: string | null;
  customerVisibleDetails: string | null;
  preliminaryCharges: CheckInChargeLine[];
  chargesTotal: number;
}

export interface CheckInPublicCustomerUpdateRequest {
  /**
   * Both required — the intake form collects them as separate fields and the
   * server derives `customerName`, which this request deliberately cannot set.
   */
  customerFirstName: string;
  customerLastName: string;
  customerEmail: string;
  customerPhone: string;
  businessName?: string;
  customerNotes?: string;

  /**
   * Where a disposal notice should be served, if the customer chose. Omitted
   * leaves whatever the session already has, which reads as `sms`.
   *
   * It rides along with the contact details rather than being saved when the
   * customer picks it: this request requires the four contact fields, and the
   * choice is offered a step earlier, before any of them have been collected.
   * Sending it here also means the preference and the details land in one
   * write rather than two.
   *
   * Unlike every other field here, an unrecognised value is **rejected** rather
   * than ignored — it records where a statutory notice will be served, so a
   * silently dropped choice would leave the customer believing they had picked
   * email.
   */
  disposalNoticeChannel?: CheckInDisposalNoticeChannel;

  /**
   * The customer's high-value declaration. Omitted leaves whatever the session
   * has; `false` is a meaningful value and worth sending, because it records a
   * customer who was asked and said no rather than one never asked.
   *
   * Strictly boolean — the server rejects strings and numbers rather than
   * coercing them. `Boolean("false")` is `true`, and the NCAT protection this
   * triggers is not something to infer from a truthy value.
   */
  highValueDeclared?: boolean;
}

export interface CheckInPublicAcceptTermsRequest {
  /**
   * The version the SWA displayed — an ECHO the server validates against the
   * active template. On mismatch the server returns 409 (terms changed since
   * the page loaded) and the SWA re-fetches. The server is authoritative on
   * the version + body it actually snapshots.
   */
  termsVersion: string;
  acceptedTotal: number;              // Server validates against the live charges total
}

/**
 * Active terms shown on the intake page (GET .../terms), resolved for the
 * session's own intake type.
 */
export interface CheckInPublicTerms {
  intakeType: 'in_store' | 'online';
  version: string;
  /**
   * Already resolved: `sm4rt-public-api` substitutes every `{{variable}}`
   * before responding. The page renders this, it does not interpolate it —
   * the server is authoritative about the text, because the same resolution
   * produces the body it will freeze onto the acceptance.
   */
  body: string;
  closer: string | null;
  /** How to render `body` / `closer`. See {@link TermsBodyFormat}. */
  bodyFormat: TermsBodyFormat;
}

/**
 * Returning-customer OTP — step one.
 *
 * The mobile is supplied by the customer, because it is the thing being
 * identified: a wall-QR session has no number on it yet. Sending a code to a
 * number someone else owns tells the sender nothing — the code goes to that
 * handset, and verifying only ever reveals that number's own details.
 */
export interface CheckInPublicOtpRequest {
  channel: 'sms';
  /** As typed. Normalised to E.164 server-side before it is bound to the code. */
  mobile: string;
}

export interface CheckInPublicOtpRequestResponse {
  sent: true;
  channel: 'sms' | 'email';
  /** How long the code lasts, for the page's countdown. Never the code itself. */
  expiresInSeconds: number;
}

/**
 * Step two. The mobile is re-supplied and checked against the one the code was
 * issued to, so a code sent to your own phone cannot be redeemed against
 * somebody else's number.
 */
export interface CheckInPublicOtpVerifyRequest {
  mobile: string;
  code: string;
}

/**
 * `verified: true` means the prefill has been **asked for**, not that it has
 * arrived: the lookup runs in the trusted zone, so the page re-reads its session
 * to collect it. Nothing about the customer is returned here — the public API
 * cannot read customers, which is why the round trip exists at all.
 *
 * Every failure answers `{ verified: false }` with a 200 — wrong code, expired,
 * too many attempts, wrong mobile, and no code ever issued alike.
 */
export interface CheckInPublicOtpVerifyResponse {
  verified: boolean;
}

export interface CheckInPublicPaymentLinkResponse {
  method: 'xero_hosted';
  xeroOnlineInvoiceUrl: string;
}
